import { getStoredJwt } from "./authApi";

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = atob(base64);
  const output = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    output[i] = rawData.charCodeAt(i);
  }
  return output;
}

let pushSyncDone = false;

export function pushNotificationsSupported(): boolean {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

export async function fetchPushServerEnabled(): Promise<boolean> {
  try {
    const res = await fetch("/api/push/vapid-public-key");
    if (!res.ok) return false;
    const j = (await res.json()) as { enabled?: boolean };
    return j.enabled === true;
  } catch {
    return false;
  }
}

async function getVapidPublicKey(): Promise<string | null> {
  try {
    const res = await fetch("/api/push/vapid-public-key");
    if (!res.ok) return null;
    const j = (await res.json()) as { publicKey: string | null; enabled?: boolean };
    return j.enabled && j.publicKey ? j.publicKey : null;
  } catch {
    return null;
  }
}

async function sendSubscriptionToServer(sub: PushSubscription): Promise<boolean> {
  const jwt = getStoredJwt();
  if (!jwt) return false;
  try {
    const res = await fetch("/api/push/subscribe", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${jwt}` },
      body: JSON.stringify(sub.toJSON()),
    });
    if (!res.ok) {
      console.warn("[styx push] subscribe POST", res.status);
      return false;
    }
    return true;
  } catch (e) {
    console.warn("[styx push] subscribe network", e);
    return false;
  }
}

/**
 * After login: if the user already granted permission, ensure a valid subscription
 * exists and is registered with the relay. Runs at most once per page load.
 */
export async function syncPushSubscriptionIfGranted(): Promise<void> {
  if (pushSyncDone) return;
  if (!pushNotificationsSupported()) return;
  if (Notification.permission !== "granted") return;

  const vapidKey = await getVapidPublicKey();
  if (!vapidKey) return;

  let registration: ServiceWorkerRegistration;
  try {
    registration = await navigator.serviceWorker.ready;
  } catch {
    return;
  }

  let subscription = await registration.pushManager.getSubscription();
  if (!subscription) {
    try {
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidKey) as BufferSource,
      });
    } catch (e) {
      console.warn("[styx push] subscribe failed", e);
      return;
    }
  }

  const serverOk = await sendSubscriptionToServer(subscription);
  if (serverOk) pushSyncDone = true;
}

/**
 * Call only from a click/tap handler (user gesture required for Android Chrome).
 */
export async function enablePushNotificationsFromUserGesture(): Promise<{ ok: boolean; message?: string }> {
  if (!pushNotificationsSupported()) {
    return { ok: false, message: "Браузер не поддерживает push." };
  }
  if (Notification.permission === "denied") {
    return { ok: false, message: "Уведомления заблокированы в настройках браузера." };
  }
  if (Notification.permission === "default") {
    const perm = await Notification.requestPermission();
    if (perm !== "granted") {
      return { ok: false, message: "Разрешение на уведомления не выдано." };
    }
  }

  const vapidKey = await getVapidPublicKey();
  if (!vapidKey) {
    return { ok: false, message: "Сервер push не настроен." };
  }

  let registration: ServiceWorkerRegistration;
  try {
    registration = await navigator.serviceWorker.ready;
  } catch {
    return { ok: false, message: "Service Worker не готов." };
  }

  let subscription = await registration.pushManager.getSubscription();
  if (!subscription) {
    try {
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidKey) as BufferSource,
      });
    } catch (e) {
      console.warn("[styx push] subscribe failed", e);
      return { ok: false, message: "Не удалось подписаться." };
    }
  }

  const ok = await sendSubscriptionToServer(subscription);
  if (ok) pushSyncDone = true;
  return ok ? { ok: true } : { ok: false, message: "Не удалось зарегистрировать подписку на сервере." };
}

export async function unsubscribePushNotifications(): Promise<void> {
  if (typeof window === "undefined") return;
  if (!("serviceWorker" in navigator)) return;
  const jwt = getStoredJwt();
  if (!jwt) {
    pushSyncDone = false;
    return;
  }
  try {
    const registration = await navigator.serviceWorker.ready;
    const sub = await registration.pushManager.getSubscription();
    if (!sub) return;
    await fetch("/api/push/unsubscribe", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${jwt}` },
      body: JSON.stringify({ endpoint: sub.endpoint }),
    });
    await sub.unsubscribe();
  } catch {
    /* ignore */
  } finally {
    pushSyncDone = false;
  }
}

/**
 * Unsubscribe and subscribe again, then register with the server. Use from a button after push stops working.
 */
export async function forceResubscribePush(): Promise<{ ok: boolean; message?: string }> {
  if (!pushNotificationsSupported()) {
    return { ok: false, message: "Браузер не поддерживает push." };
  }
  if (Notification.permission === "denied") {
    return { ok: false, message: "Уведомления заблокированы в настройках браузера." };
  }
  if (Notification.permission === "default") {
    return { ok: false, message: "Сначала разрешите уведомления." };
  }
  const jwt = getStoredJwt();
  if (!jwt) {
    return { ok: false, message: "Нужен вход в аккаунт." };
  }

  pushSyncDone = false;
  await unsubscribePushNotifications();

  const vapidKey = await getVapidPublicKey();
  if (!vapidKey) {
    return { ok: false, message: "Сервер push не настроен." };
  }

  let registration: ServiceWorkerRegistration;
  try {
    registration = await navigator.serviceWorker.ready;
  } catch {
    return { ok: false, message: "Service Worker не готов." };
  }

  let subscription: PushSubscription;
  try {
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidKey) as BufferSource,
    });
  } catch (e) {
    console.warn("[styx push] force resubscribe subscribe failed", e);
    return { ok: false, message: "Не удалось подписаться снова." };
  }

  const ok = await sendSubscriptionToServer(subscription);
  if (ok) pushSyncDone = true;
  return ok ? { ok: true } : { ok: false, message: "Не удалось зарегистрировать подписку на сервере." };
}
